import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import DeleteModal from '../../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { crudeApi, proHydroApi } from '../../../helpers/crude_api_helper';

// Backend entity: CDU (Crude Distillation Unit) — Crude / Master Data.
// See api-contract-cdu.md. CDU is NOT direct CRUD: create/update/delete each
// raise a ChangeRequest (AwaitingReview) that must be reviewed + approved before
// it takes effect. Consequences reflected in this UI:
//   - Create: new row does NOT appear in the list until approved (toast only).
//   - Update/Delete: the row stays with its OLD values and dataStatus 'OnProcess'
//     until approved; while 'OnProcess' its Edit/Delete actions are locked.
// Write endpoints bind with [FromForm] → send url-encoded form (postForm/putForm),
// NOT JSON. PUT has no {id} route param — the id goes in the form body.
// ⚠️ CROSS-HOST: this one screen spans both backends (same split as the report
// Advance Search — see Components/crude/reportConstants.js).
//   - CDU list + writes            -> `crudeApi`    (rhacrude_api, 06.WebApi.Crude)
//   - Refinery Unit ("Plant") lookup -> `proHydroApi` (rha_api, 06.WebApi)
// Verified by curl 2026-07-27: /v1/CDU/GetListCDU is 404 on rha_api and 401 on
// rhacrude_api; /v1/RefineryUnits/Lookup is the exact inverse. They are not
// interchangeable — sending either to the other host 404s silently.
const CDU_ENDPOINT = '/v1/CDU';
const CDU_LIST_ENDPOINT = '/v1/CDU/GetListCDU';
const REFINERY_UNIT_LOOKUP = '/v1/RefineryUnits/Lookup';

// Server-side sort whitelist (PascalCase DTO property names — wrong casing → 400).
const SORT_FIELDS = {
  plant: 'PlantName',
  cduName: 'CDUName',
  orderNumber: 'OrderNumber',
  description: 'Description',
};

const emptyForm = {
  id: null,
  plantId: '',
  cduName: '',
  orderNumber: '',
  description: '',
};

const CduView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refineryUnits, setRefineryUnits] = useState([]);

  const [modalForm, setModalForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Server-side pagination / search / sort state.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  document.title = 'CDU';

  // Debounce the search box, then reset to page 1 for a fresh server query.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchKeyword.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await crudeApi.get(CDU_LIST_ENDPOINT, {
        page,
        pageSize,
        searchText: debouncedSearch || undefined,
        sortField: sortField || undefined,
        sortOrder: sortField ? sortOrder : undefined,
      });
      setItems(Array.isArray(result?.items) ? result.items : []);
      setTotalCount(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sortField, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchRefineryUnits = async () => {
    try {
      const result = await proHydroApi.get(REFINERY_UNIT_LOOKUP);
      setRefineryUnits(Array.isArray(result?.items) ? result.items : []);
    } catch (error) {
      toast.error(`Failed to load refinery units: ${error}`);
      setRefineryUnits([]);
    }
  };

  useEffect(() => {
    fetchRefineryUnits();
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  // Client-side pre-check for a nicer UX; the server is the source of truth and
  // returns 409 AlreadyExists on a duplicate cduName within the same Plant.
  const isDuplicate = () => {
    const name = (formData.cduName || '').trim().toLowerCase();
    return items.some(
      row =>
        row.plantId === formData.plantId &&
        (row.cduName || '').trim().toLowerCase() === name &&
        row.id !== formData.id
    );
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.cduName || !formData.cduName.trim()) {
      newErrors.cduName = 'CDU Name is required';
    } else if (formData.cduName.trim().length > 250) {
      newErrors.cduName = 'CDU Name max 250 characters';
    }
    if (!formData.plantId) {
      newErrors.plantId = 'Refinery Unit is required';
    }
    if (
      formData.orderNumber === '' ||
      formData.orderNumber === null ||
      formData.orderNumber === undefined
    ) {
      newErrors.orderNumber = 'Order Number is required';
    } else if (Number(formData.orderNumber) === 0) {
      newErrors.orderNumber = 'Order Number cannot be 0';
    }
    if (
      !newErrors.cduName &&
      formData.plantId &&
      formData.cduName &&
      isDuplicate()
    ) {
      newErrors.cduName = 'CDU Name already exists in this Plant';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewModal = () => {
    setFormData(emptyForm);
    setErrors({});
    setModalForm(true);
  };

  const handleUpdateModal = row => {
    if (row.dataStatus === 'OnProcess') return; // locked while pending review/approval
    setFormData({
      id: row.id,
      plantId: row.plantId || '',
      cduName: row.cduName || '',
      orderNumber: row.orderNumber ?? '',
      description: row.description || '',
    });
    setErrors({});
    setModalForm(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);

    const payload = {
      plantId: formData.plantId,
      cduName: formData.cduName.trim(),
      orderNumber: formData.orderNumber,
      description: formData.description || '',
    };

    if (formData.id) {
      try {
        // Update → PUT form (id in body, no route param). Submits a ChangeRequest;
        // the row stays with old values (dataStatus 'OnProcess') until approved.
        await crudeApi.putForm(CDU_ENDPOINT, {
          id: formData.id,
          ...payload,
        });
        toast.success('CDU change submitted for review');
        setModalForm(false);
        fetchData();
      } catch (error) {
        toast.error(`Failed to update data: ${error}`);
      } finally {
        setSaving(false);
      }
    } else {
      try {
        // Create → POST form. Response is only { cduId }; the new CDU does not
        // appear in the list until approved (visible via ChangeRequest My List).
        await crudeApi.postForm(CDU_ENDPOINT, payload);
        toast.success('CDU submitted for review');
        setModalForm(false);
        fetchData();
      } catch (error) {
        toast.error(`Failed to save data: ${error}`);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDeleteModal = row => {
    if (row.dataStatus === 'OnProcess') return; // locked while pending review/approval
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      // Delete → submits a ChangeRequest. The row stays ('OnProcess') until the
      // deletion is approved, then it is soft-deleted and drops off the list.
      await crudeApi.delete(`${CDU_ENDPOINT}/${selectedRow.id}`);
      toast.success('CDU deletion submitted for review');
    } catch (error) {
      toast.error(`Failed to delete data: ${error}`);
    } finally {
      setDeleteModal(false);
      setSelectedRow(null);
      fetchData();
    }
  };

  // Server-side sort: each data column carries a PascalCase `sortField`.
  const handleSort = (column, sortDirection) => {
    if (!column?.sortField) return;
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 'Desc' : 'Asc');
    setPage(1);
  };

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const columns = [
    {
      name: headerLabel('Plant'),
      selector: row => row.plantName,
      sortable: true,
      sortField: SORT_FIELDS.plant,
      wrap: true,
    },
    {
      name: headerLabel('CDU Name'),
      selector: row => row.cduName,
      sortable: true,
      sortField: SORT_FIELDS.cduName,
      wrap: true,
    },
    {
      name: headerLabel('Order Number'),
      selector: row => row.orderNumber,
      sortable: true,
      sortField: SORT_FIELDS.orderNumber,
      width: '150px',
      center: true,
    },
    {
      name: headerLabel('Description'),
      selector: row => row.description,
      sortable: true,
      sortField: SORT_FIELDS.description,
      wrap: true,
      grow: 2,
    },
    {
      name: headerLabel('Action'),
      width: '120px',
      center: true,
      cell: row => {
        const locked = row.dataStatus === 'OnProcess';
        const title = locked ? 'Sedang dalam review & approval' : undefined;
        return (
          <div className='d-flex gap-1 justify-content-end'>
            <button
              className={`btn btn-sm btn-icon ${
                locked ? 'btn-soft-secondary' : 'btn-soft-info'
              }`}
              onClick={() => handleUpdateModal(row)}
              disabled={locked}
              title={title}
              style={
                locked ? { opacity: 0.5, cursor: 'not-allowed' } : undefined
              }
            >
              <i className='ri-pencil-fill align-bottom'></i>
            </button>
            <button
              className={`btn btn-sm btn-icon ${
                locked ? 'btn-soft-secondary' : 'btn-soft-danger'
              }`}
              onClick={() => handleDeleteModal(row)}
              disabled={locked}
              title={title}
              style={
                locked ? { opacity: 0.5, cursor: 'not-allowed' } : undefined
              }
            >
              <i className='ri-delete-bin-6-line align-bottom'></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <Container fluid>
          <BreadCrumb title='CDU' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='CduList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>CDU</h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search...'
                            value={searchKeyword}
                            onChange={e => setSearchKeyword(e.target.value)}
                          />
                        </div>
                        <button
                          type='button'
                          className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                          onClick={handleNewModal}
                        >
                          <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                          <span className='d-none d-sm-inline'>Add CDU</span>
                          <span className='d-sm-none'>New</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      progressPending={loading}
                      pagination
                      paginationServer
                      paginationTotalRows={totalCount}
                      paginationDefaultPage={page}
                      paginationPerPage={pageSize}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={p => setPage(p)}
                      onChangeRowsPerPage={(newPerPage, p) => {
                        setPageSize(newPerPage);
                        setPage(p);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No CDU found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={modalForm}
            role='dialog'
            autoFocus={true}
            centered
            id='addCduModal'
            toggle={() => setModalForm(!modalForm)}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalForm(!modalForm)}
            >
              <span className='modal-title text-info fw-bold'>
                {formData.id !== null ? 'Edit CDU' : 'Add CDU'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='cduName' className='form-label'>
                          CDU Name <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='cduName'
                          name='cduName'
                          className='form-control'
                          maxLength={250}
                          invalid={!!errors.cduName}
                          value={formData.cduName || ''}
                          onChange={handleChange}
                        />
                        {errors.cduName && (
                          <div className='invalid-feedback d-block'>
                            {errors.cduName}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='plantId' className='form-label'>
                          Refinery Unit <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='select'
                          id='plantId'
                          name='plantId'
                          className='form-control'
                          invalid={!!errors.plantId}
                          value={formData.plantId || ''}
                          onChange={handleChange}
                        >
                          <option value=''>Select Refinery Unit</option>
                          {refineryUnits.map(opt => (
                            <option key={opt.id} value={opt.id}>
                              {opt.text}
                            </option>
                          ))}
                        </Input>
                        {errors.plantId && (
                          <div className='invalid-feedback d-block'>
                            {errors.plantId}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='orderNumber' className='form-label'>
                          Order Number <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='number'
                          id='orderNumber'
                          name='orderNumber'
                          className='form-control'
                          min={1}
                          invalid={!!errors.orderNumber}
                          value={formData.orderNumber ?? ''}
                          onChange={handleChange}
                        />
                        {errors.orderNumber && (
                          <div className='invalid-feedback d-block'>
                            {errors.orderNumber}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='description' className='form-label'>
                          Description
                        </Label>
                        <Input
                          type='text'
                          id='description'
                          name='description'
                          className='form-control'
                          maxLength={255}
                          value={formData.description || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </Col>
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color='light'
                className='btn-sm'
                onClick={() => setModalForm(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                color='primary'
                className='btn-sm'
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CduView;
